import { Checkbox, Stack, useDisclosure, useToast } from '@chakra-ui/react';
import { FreightService, FreightServiceAddOn } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { getPrettyFreightServiceAddOn } from '@pelicargo/utils';
import { uniq } from 'lodash';
import { ChangeEvent, useCallback, useMemo } from 'react';

import { useUpdateRequest } from '../../hooks/request/useUpdate';
import { Request } from '../../utils/types';
import { CreateQuoteModal } from '../CreateQuoteModal';

type Props = {
  request: Pick<
    Request,
    | 'id'
    | 'author'
    | 'freight_service_add_ons'
    | 'origin_airports'
    | 'destination_airports'
    | 'account_id'
    | 'is_consolidated'
  >;
};

export const InlandQuotesListHeader = ({ request }: Props) => {
  const createQuoteModalDisclosure = useDisclosure();
  const { mutate: updateRequest } = useUpdateRequest(request?.id);
  const toast = useToast();

  const originAirportId = useMemo(() => {
    return request?.origin_airports?.[0]?.id;
  }, [request?.origin_airports]);
  const destinationAirportId = useMemo(() => {
    return request?.destination_airports?.[0]?.id;
  }, [request?.destination_airports]);

  const onConsolidatedToggle = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      await updateRequest({
        is_consolidated: event.target.checked,
      });
      toast({
        title: 'Successfully updated',
        status: 'success',
        duration: 1000,
        isClosable: true,
      });
    },
    [toast, updateRequest],
  );

  const onFreightServiceAddOnChange = useCallback(
    async (event: any) => {
      const nextFreightServiceAddOn = request?.freight_service_add_ons || [];

      if (event.target.checked) {
        nextFreightServiceAddOn.push(event.target.id);
      } else {
        const index = nextFreightServiceAddOn.indexOf(event.target.id);
        if (index > -1) {
          nextFreightServiceAddOn.splice(index, 1);
        }
      }

      try {
        await updateRequest({
          freight_service_add_ons: uniq(nextFreightServiceAddOn),
        });

        toast({
          title: 'Successfully updated',
          status: 'success',
          duration: 1000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: 'Error updating',
          description: error?.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [request?.freight_service_add_ons, toast, updateRequest],
  );

  const renderFreightServiceAddOn = useCallback(
    (addon: FreightServiceAddOn) => {
      return (
        <Checkbox
          key={addon}
          id={addon}
          tabIndex={0}
          defaultChecked={request?.freight_service_add_ons?.includes(addon)}
          onChange={onFreightServiceAddOnChange}
        >
          {getPrettyFreightServiceAddOn(addon)}
        </Checkbox>
      );
    },
    [onFreightServiceAddOnChange, request?.freight_service_add_ons],
  );

  return (
    <Stack direction="row" p="2" pr="4" pl="8" justifyContent="space-between" w="full">
      <Stack direction="row" alignItems="center" spacing="4" w="full" overflowX="auto">
        {Object.values(FreightServiceAddOn).map(renderFreightServiceAddOn)}
        <Checkbox tabIndex={0} defaultChecked={request?.is_consolidated} onChange={onConsolidatedToggle}>
          HAWB Required
        </Checkbox>
      </Stack>
      <Show if={createQuoteModalDisclosure.isOpen}>
        <CreateQuoteModal
          request={request}
          initialOriginAirportId={originAirportId}
          initialDestinationAirportId={destinationAirportId}
          freightService={FreightService.EXW}
          {...createQuoteModalDisclosure}
        />
      </Show>
    </Stack>
  );
};
